import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { StateService, UIRouterGlobals } from '@uirouter/core';
import { btgPlanNames, CONFIG_TOKEN } from 'core/constants';
import { Subscription } from 'rxjs';
import { CimaCore } from 'services/cima/core';
import { Cloudinary } from 'services/cloudinary';
import { XmStore, XmStoreUtil } from 'services/store';
import { Util } from 'services/util';
import { AccountSummary, LineSummary } from 'store/account-summary/models';
import { CmsLineDataPlanCollection, CmsPlanSwitch } from 'store/cms/models';
import { DiscountPromoCollection, DiscountPromotion } from 'store/promotions/discount-promotion/models';
import { IXMOptions } from 'core/interfaces';
import { targetPlanSwitchDetails } from 'store/plan-eligibility/models/target-plan-switch';
import { User } from 'store/user/models';

@Component({
    selector: 'xm-btg-target-plan-review',
    styleUrls: ['./btg-plan-review.scss'],
    templateUrl: 'btg-plan-review.html',
    standalone: false
})
    
export class XmBtgTargetPlanSwitchReviewComponent implements OnInit, OnDestroy {
    public pageData: targetPlanSwitchDetails;
    public btgPlan: PlanCardsDetails;
    public cmsPageData: CmsDetailPlanFeature[];
    public dataPlanCollection: CmsLineDataPlanCollection;
    public selectedPlanData: CmsDataPlanDetails;
    public user: User;
    //promo
    public promoResponse: DiscountPromoCollection;
    public isDiscountPromotion: boolean = false;
    public promotionType: string;
    public promoContent: string;
    public promoBYOContent: string;
    public promoUNLContent: string;
    public tradeInDDAPromoContent: string;
    public extendedPromoContent: string;
    public hasSwitchPromoBYO: boolean = false;
    public lineSummary: LineSummary;
    public showBogoPromoLossForUnlLines: boolean = false;
    public isIcopPlanSwitch: boolean = false;
    public isBillPreviewEnabled: boolean = false;
    public switchCta: string;
    public showSegmentUpgradeBanner: boolean;
    public segmentSwitchBanner: NotificationBanner;
    public segmentSwitchIcon: MediaImageOptions;

    private subscriptions: Subscription[] = [];
    private xmStore: XmStore;
    private state: StateService;
    private globalRouterState: UIRouterGlobals;
    private cimaCore: CimaCore;
    private config: IXMOptions;

    constructor(xmStore: XmStore, state: StateService, globalRouterState: UIRouterGlobals, cimaCore: CimaCore, @Inject(CONFIG_TOKEN) config: IXMOptions) {
        Object.assign(this, { xmStore, state, globalRouterState, cimaCore, config });
    }

    public ngOnInit(): void {
        this.isIcopPlanSwitch = this.config.ENABLE_ICOP_P1_PLANSWITCH;
        this.isBillPreviewEnabled = (this.config.ENABLE_BILL_PREVIEW && !this.cimaCore.isBusinessUser) || (this.config.ENABLE_CBM_BILL_PREVIEW && this.cimaCore.isBusinessUser);

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<User>(User), (user: User) => {
            this.user = user;
            this.showSegmentUpgradeBanner = (user.account.canMoveLevel || ((user.account.isSegmentFive && !this.cimaCore.isBusinessUser)
                && !user.account.isNYCustomer));
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<targetPlanSwitchDetails>(targetPlanSwitchDetails), (result: targetPlanSwitchDetails) => {
            this.pageData = result;
            this.switchCta = this.isBillPreviewEnabled ? this.pageData.selectionPage.ctas.reviewDetails : this.pageData.selectionPage.ctas.switch;
            this.btgPlan = this.pageData.selectionPage.planCards.find((plan: PlanCardsDetails) => btgPlanNames.includes(plan.name.toUpperCase()));
            this.btgPlan.type = Util.createPascalCase(this.btgPlan.type);
            if (this.showSegmentUpgradeBanner) {
                this.segmentSwitchBanner = this.pageData.selectionPage?.banners.length ? this.pageData.selectionPage.banners[0] : undefined;
                this.segmentSwitchIcon = {
                    mobile: {
                        base: this.segmentSwitchBanner?.image
                    }
                };
            }
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsLineDataPlanCollection>(CmsLineDataPlanCollection), (result: CmsLineDataPlanCollection) => {
            this.dataPlanCollection = result;
            const planCode: string = decodeURIComponent(this.state.params.plan_code);
            this.selectedPlanData = this.dataPlanCollection.plans.find((cmsPlan: CmsDataPlanDetails) => cmsPlan.planCode.includes(planCode));
            this.cmsPageData = this.selectedPlanData.planSixSegFiveFeatures[0].features;
        }));
        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<AccountSummary>(AccountSummary), (summary: AccountSummary) => {
            this.lineSummary = summary.getLineByKey(this.state.params.line_id);
            this.showBogoPromoLossForUnlLines = this.lineSummary.numberOfLinesInThePlan < 3;
        }));
        if (!this.isIcopPlanSwitch) {
            this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<DiscountPromoCollection>(DiscountPromoCollection), (result: DiscountPromoCollection) => {
                this.promoResponse = result;
                this.planSwitchPromoEligible();
            }));
        }
    }

    public getMediaImageOptions(icon: CmsMediaImage): MediaImageOptions {
        return {
            mobile: Cloudinary.generateMediaFromCms(icon)
        };
    }

    public ngOnDestroy(): void {
        Util.unsubscribeAll(this.subscriptions);
    }

    public onCancel(): void {
        this.state.go('account.activity.details', { line_id: this.globalRouterState.params.line_id });
    }
    
    public navToSwitchInProgress(): void {
        if (this.isBillPreviewEnabled) {
            this.state.go('account.activity.details.plan-switch.review-order', { line_id: this.globalRouterState.params.line_id, plan_code: this.btgPlan.planCode, backButtonPath: 'account.activity.details.plan-switch.xm-btg-target-plan-review' });
        } else {
            this.state.go('account.activity.details.plan-switch.in-progress', { line_id: this.globalRouterState.params.line_id, plan_code: this.btgPlan.planCode }); 
        }
    } 
    
    // Displaying promo container temp. Needs to be replaced post CEUIDSS-29611 & CEUIDSS-29620 completion
    public planSwitchPromoEligible(): void {
        this.hasSwitchPromoBYO = this.promoResponse?.isLinePromoBYOEligible ?? false;  // performing null check if the promoResponse is not available
        this.isDiscountPromotion = this.promoResponse && (this.promoResponse.isLinePromoEligible || this.promoResponse.isAccountPromoEligible || this.promoResponse.isTradeInDDAPromoEligible || this.promoResponse.isExtendedPromoDollarEligible);
        if (this.isDiscountPromotion) {
            this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsPlanSwitch>(CmsPlanSwitch), (resp: CmsPlanSwitch) => {
                const promo: CmsPlanSwitchMultiplePromotions = resp.planSwitchMultiplePromotions;
                this.promoContent = promo.reviewDescription.general;
                this.promoBYOContent = this.promoResponse.isLinePromoBYOEligible ? promo.reviewDescription.BYO : undefined;
                if (this.promoResponse.isLinePromoUNLFREEEligible || this.promoResponse.isLinePromoUNLHL25Eligible) {
                    const servicePromoLine: DiscountPromotion = this.promoResponse.mvnoPromotions.find((prom: DiscountPromotion) => prom.type === 'SERVICE_PROMO_LINE' 
                        && (prom.lineId === this.lineSummary.id.key || prom.lineId === this.lineSummary.id.value));
                    if (servicePromoLine) {
                        this.promoUNLContent = this.cimaCore.isBusinessUser ? promo.reviewCBMDescription.UNLFREE : promo.reviewDescription.UNLFREE;
                        this.promoUNLContent = this.promoUNLContent.replace('[PROMOTION_TERM]', servicePromoLine.promotionTerm.toString());
                    }
                } else if (this.promoResponse.isAccountPromoEligible) {
                    this.promoUNLContent = promo.reviewDescription.UNL;
                }
                const tradeInDDAPromo: DiscountPromotion[] = this.promoResponse.tradeInDDAPromoEligible.filter((data: DiscountPromotion) => data.lineId === this.lineSummary.id.key || data.lineId === this.lineSummary.id.value);
                const extendedDDAPromo: DiscountPromotion[] = this.promoResponse.extendedPromoDollarEligible.filter((data: DiscountPromotion) => data.lineId === this.lineSummary.id.key || data.lineId === this.lineSummary.id.value);

                if (tradeInDDAPromo.length > 0) {
                    if (this.cimaCore.isBusinessUser) {
                        this.promoContent = promo.reviewCBMDescription.general;
                        this.tradeInDDAPromoContent = promo.reviewCBMDescription.DDA.replace('[PROMO_AMOUNT]', tradeInDDAPromo[0].billerInfo.promotionAmount.toString());
                    } else {
                        this.tradeInDDAPromoContent = promo.reviewDescription.DDA.replace('[PROMO_AMOUNT]', tradeInDDAPromo[0].billerInfo.promotionAmount.toString());
                    }
                }
                if (extendedDDAPromo.length > 0) {
                    if (this.cimaCore.isBusinessUser) {
                        this.promoContent = promo.reviewCBMDescription.general;
                        this.extendedPromoContent = promo.reviewCBMDescription.EPD.replace('[PROMO_AMOUNT]', extendedDDAPromo[0].billerInfo.promotionAmount.toString());
                    } else {
                        this.extendedPromoContent = promo.reviewDescription.EPD.replace('[PROMO_AMOUNT]', extendedDDAPromo[0].billerInfo.promotionAmount.toString());
                    }
                }
            }));
        }
    }
}
